import React, { useEffect, useState } from 'react';
import { Button, Input, Select } from '..';
import { FiBriefcase, FiMapPin, FiHash, FiFlag, FiHome, FiUser, FiMail, FiPhone, FiFileText, FiAlignLeft, FiImage } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import './style.css';

export default function CompanyForm({ initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedLogo, setSelectedLogo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  const [formData, setFormData] = useState({
    corporate_name: '',
    trade_name: '',
    cnpj: '',
    address_street: '',
    address_number: '',
    address_district: '',
    address_city: '',
    address_state: '',
    address_zip_code: '',
  });

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      if (initialData.logo) {
        if (initialData.logo.type === 'Buffer') {
          const base64String = btoa(
            new Uint8Array(initialData.logo.data)
              .reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          setPreviewUrl(`data:image/png;base64,${base64String}`);
        } else if (typeof initialData.logo === 'string') {
          setPreviewUrl(initialData.logo);
        }
      }
    }
  }, [initialData]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  function handleFileChange(event) {
    const file = event.target.files[0];
    if (file) {
      setSelectedLogo(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  }

  function validateForm() {
    const messages = [];
    const cnpjDigits = (formData.cnpj || '').replace(/\D/g, '');

    if (!formData.corporate_name || !formData.corporate_name.trim()) {
      messages.push('Informe a Razão Social.');
    }

    if (!cnpjDigits || cnpjDigits.length !== 14) {
      messages.push('Informe um CNPJ válido.');
    }

    if (!formData.address_zip_code || !formData.address_zip_code.trim()) {
      messages.push('Informe o CEP.');
    }

    if (!formData.address_street || !formData.address_street.trim()) {
      messages.push('Informe a Rua / Avenida.');
    }

    if (!formData.address_number || !formData.address_number.toString().trim()) {
      messages.push('Informe o Número.');
    }

    if (!formData.address_city || !formData.address_city.trim()) {
      messages.push('Informe a Cidade.');
    }

    if (!formData.address_state || !formData.address_state.trim()) {
      messages.push('Selecione o Estado (UF).');
    }

    if (!formData.owner_name || !formData.owner_name.trim()) {
      messages.push('Informe o Nome do responsável.');
    }

    if (!formData.owner_email || !formData.owner_email.trim()) {
      messages.push('Informe o E-mail do responsável.');
    } else {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(formData.owner_email)) {
        messages.push('Informe um E-mail válido.');
      }
    }

    if (!formData.owner_phone || !formData.owner_phone.trim()) {
      messages.push('Informe o Telefone / Celular do responsável.');
    }

    if (messages.length > 0) {
      messages.forEach(message => toast.error(message));
      return false;
    }

    return true;
  }

  async function handleCnpjBlur(event) {
    const cnpjValue = event.target.value.replace(/\D/g, '');

    if (cnpjValue.length === 14) {
      setCnpjLoading(true);
      try {
        const response = await fetch(`https://brasilapi.com.br/api/cnpj/v1/${cnpjValue}`);
        const data = await response.json();

        if (!data.message) {
          const formattedZip = data.cep ? data.cep.replace(/\D/g, '').replace(/^(\d{5})(\d{3})/, '$1-$2') : '';

          setFormData(prev => ({
            ...prev,
            corporate_name: data.razao_social || prev.corporate_name,
            trade_name: data.nome_fantasia || data.razao_social || prev.trade_name,
            address_zip_code: formattedZip || prev.address_zip_code,
            address_street: data.logradouro || prev.address_street,
            address_number: data.numero || prev.address_number,
            address_complement: data.complemento || prev.address_complement,
            address_district: data.bairro || prev.address_district,
            address_city: data.municipio || prev.address_city,
            address_state: data.uf || prev.address_state,
            owner_name: data.qsa && data.qsa.length > 0 ? data.qsa[0].nome_socio : prev.owner_name,
          }));
        }
      } catch (error) {
        console.error('Erro ao buscar CNPJ', error);
      } finally {
        setCnpjLoading(false);
      }
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    const isValid = validateForm();
    if (!isValid) return;
    setLoading(true);

    try {
      const data = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined && key !== 'logo') {
          data.append(key, formData[key]);
        }
      });
      
      if (selectedLogo) {
        data.append('logo', selectedLogo);
      }

      let result;
      if (isEdit) {
        result = await Api.updateCompany(initialData.id, data);
      } else {
        result = await Api.createCompany(data);
      }

      if (result && !result.error) {
        if (onSuccess) onSuccess(result);
      } else {
        setError(result.error || 'Erro ao salvar empresa.');
      }
    } catch (err) {
      setError('Erro ao processar requisição.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="company-form-component">
      <div className="form-body">
        <div className="logo-upload-section">
          <div className="logo-preview">
            {previewUrl ? (
              <img src={previewUrl} alt="Logo Preview" />
            ) : (
              <div className="logo-placeholder">
                <FiImage size={40} />
              </div>
            )}
          </div>
          <div className="logo-actions">
            <label htmlFor="logo-upload" className="logo-upload-btn">
              Selecionar Logo
            </label>
            <input 
              id="logo-upload" 
              type="file" 
              accept="image/*" 
              onChange={handleFileChange} 
              style={{ display: 'none' }} 
            />
            <span className="logo-help-text">JPG, PNG (Max 5MB)</span>
          </div>
        </div>

        <div className="form-grid">
          <div className="full-width">
            <Input
              id="corporate_name"
              label="Razão Social *"
              value={formData.corporate_name}
              onChange={handleChange}
              placeholder="Ex: Maxximus Tecnologia LTDA"
              required
              icon={FiBriefcase}
            />
          </div>
          <Input
            id="trade_name"
            label="Nome Fantasia"
            value={formData.trade_name}
            onChange={handleChange}
            placeholder="Ex: Maxximus Tech"
            icon={FiBriefcase}
          />
          <Input
            id="cnpj"
            label={cnpjLoading ? "CNPJ (Buscando...)" : "CNPJ"}
            value={formData.cnpj}
            onChange={handleChange}
            onBlur={handleCnpjBlur}
            placeholder="00.000.000/0000-00"
            icon={FiHash}
            mask="cnpj"
          />
        </div>

        <h3 className="section-title">Endereço</h3>
        <div className="form-grid">
          <Input
            id="address_zip_code"
            label="CEP"
            value={formData.address_zip_code}
            onChange={handleChange}
            placeholder="00000-000"
            icon={FiMapPin}
            mask="cep"
          />
          <div className="full-width-mobile">
            <Input
              id="address_street"
              label="Rua / Avenida"
              value={formData.address_street}
              onChange={handleChange}
              icon={FiMapPin}
            />
          </div>
          <Input
            id="address_number"
            label="Número"
            value={formData.address_number}
            onChange={handleChange}
            icon={FiHome}
          />
          <Input
            id="address_complement"
            label="Complemento"
            value={formData.address_complement}
            onChange={handleChange}
            placeholder="Ex: Sala 101"
            icon={FiAlignLeft}
          />
          <Input
            id="address_district"
            label="Bairro"
            value={formData.address_district}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Input
            id="address_city"
            label="Cidade"
            value={formData.address_city}
            onChange={handleChange}
            icon={FiMapPin}
          />
          <Select
            id="address_state"
            label="Estado (UF)"
            value={formData.address_state || ''}
            onChange={handleChange}
            icon={FiFlag}
            searchable
            options={[
              { value: '', label: 'Selecione o estado (UF)' },
              { value: 'AC', label: 'AC - Acre' },
              { value: 'AL', label: 'AL - Alagoas' },
              { value: 'AP', label: 'AP - Amapá' },
              { value: 'AM', label: 'AM - Amazonas' },
              { value: 'BA', label: 'BA - Bahia' },
              { value: 'CE', label: 'CE - Ceará' },
              { value: 'DF', label: 'DF - Distrito Federal' },
              { value: 'ES', label: 'ES - Espírito Santo' },
              { value: 'GO', label: 'GO - Goiás' },
              { value: 'MA', label: 'MA - Maranhão' },
              { value: 'MT', label: 'MT - Mato Grosso' },
              { value: 'MS', label: 'MS - Mato Grosso do Sul' },
              { value: 'MG', label: 'MG - Minas Gerais' },
              { value: 'PA', label: 'PA - Pará' },
              { value: 'PB', label: 'PB - Paraíba' },
              { value: 'PR', label: 'PR - Paraná' },
              { value: 'PE', label: 'PE - Pernambuco' },
              { value: 'PI', label: 'PI - Piauí' },
              { value: 'RJ', label: 'RJ - Rio de Janeiro' },
              { value: 'RN', label: 'RN - Rio Grande do Norte' },
              { value: 'RS', label: 'RS - Rio Grande do Sul' },
              { value: 'RO', label: 'RO - Rondônia' },
              { value: 'RR', label: 'RR - Roraima' },
              { value: 'SC', label: 'SC - Santa Catarina' },
              { value: 'SP', label: 'SP - São Paulo' },
              { value: 'SE', label: 'SE - Sergipe' },
              { value: 'TO', label: 'TO - Tocantins' },
            ]}
          />
        </div>

        <h3 className="section-title">Dados do Responsável</h3>
        <div className="form-grid">
          <div className="full-width">
            <Input
              id="owner_name"
              label="Nome Completo"
              value={formData.owner_name}
              onChange={handleChange}
              placeholder="Nome do responsável"
              icon={FiUser}
            />
          </div>
          <Input
            id="owner_email"
            label="E-mail"
            type="email"
            value={formData.owner_email}
            onChange={handleChange}
            placeholder="email@exemplo.com"
            icon={FiMail}
          />
          <Input
            id="owner_phone"
            label="Telefone / Celular"
            value={formData.owner_phone}
            onChange={handleChange}
            placeholder="(00) 00000-0000"
            icon={FiPhone}
            mask="phone"
          />
          <div className="full-width">
            <Input
              id="owner_document"
              label="CPF / Documento"
              value={formData.owner_document}
              onChange={handleChange}
              placeholder="000.000.000-00"
              icon={FiFileText}
              mask="cpf"
            />
          </div>
        </div>
      </div>

      {error && <div className="form-error">{error}</div>}

      <div className="modal-actions">
        <Button type="button" className="cancel-button" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
